import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  MeasurementsState,
  MeasurementsCreateAction,
  MeasurementsCreateUpdateSuccessAction,
  MeasurementsUpdateAction,
  MeasurementsEmptySuccessAction,
  MeasurementsDeleteAction,
  MeasurementsHandleToolAction,
  MeasurementScreenshotSuccessAction,
  EnqueueAppendAnnotationPayload,
  EnqueueRemoveAnnotationPayload,
  EnqueueUpdateMeasurementPayload,
  LockSequencePayload,
  AppendAnnotationFailurePayload,
  RetryAppendAnnotationPayload,
} from './measurements.types';
import { createSliceHook, makeActionCreator, createSelector } from '../helpers';
import { Measurement, MeasurementReqParams } from '@dynamika-shared/types/measurements';

const initialState: MeasurementsState = {
  loading: false,
  writePending: 0,
  deletePending: false,
  activeMeasurement: null,
  error: undefined,
  data: {},
  appendPendingByMeasurement: {},
  lockPendingByMeasurement: {},
  appendRetryBufferByMeasurement: {},
  screenshotState: {},
  viewerReady: false,
  measurementsLoaded: false,
};

const { act, actEmpty, actMutate, actReq, actReject } =
  makeActionCreator<MeasurementsState>();

const incAppendPending = (
  state: MeasurementsState,
  measurementId: string
) => {
  state.appendPendingByMeasurement[measurementId] =
    (state.appendPendingByMeasurement[measurementId] ?? 0) + 1;
};

const decAppendPending = (
  state: MeasurementsState,
  measurementId: string
) => {
  const next = (state.appendPendingByMeasurement[measurementId] ?? 0) - 1;
  if (next <= 0) {
    delete state.appendPendingByMeasurement[measurementId];
  } else {
    state.appendPendingByMeasurement[measurementId] = next;
  }
};

const incLockPending = (state: MeasurementsState, measurementId: string) => {
  state.lockPendingByMeasurement[measurementId] =
    (state.lockPendingByMeasurement[measurementId] ?? 0) + 1;
};

const decLockPending = (state: MeasurementsState, measurementId: string) => {
  const next = (state.lockPendingByMeasurement[measurementId] ?? 0) - 1;
  if (next <= 0) {
    delete state.lockPendingByMeasurement[measurementId];
  } else {
    state.lockPendingByMeasurement[measurementId] = next;
  }
};

function setActiveMeasurement(
  p: MeasurementsHandleToolAction['payload'],
  state: MeasurementsState
) {
  if (p.measurementConfig.code && p.instrumentConfig.code) {
    /**
     * Remember the lesion's descriptive details ("permanent data") the moment
     * the reader arms the drawing tool.
     *
     * In plain terms
     * --------------
     * Before a radiologist draws a lesion, they pick its location and type in
     * the form. We stash those details here so that, the instant they finish
     * drawing, we can send a *complete* record to the server — which refuses to
     * save a lesion that is missing this information.
     *
     * Technical context
     * -----------------
     * The scoring form attaches `{ location, subLocation, isEnhanced }` under
     * `payload.permanentData` on every tool action (activate / append). The
     * backend rejects a create whose `permanentData` is null/absent AND
     * validates it against `permanentData.schema.json` (all three keys
     * required), so an empty `{}` also fails. By capturing the value here we
     * give `prepareRequestData` a schema-conformant fallback to send on the
     * create POST — see its `existingPermanentData` resolution.
     */
    const formPayload = p.payload as
      | {
          permanentData?: MeasurementReqParams['permanentData'];
          measurementData?: { isMeasurable?: boolean };
        }
      | undefined;

    const formPermanentData = formPayload?.permanentData;

    /**
     * Remember the measurability the reader picked, for the same reason.
     *
     * In plain terms
     * --------------
     * If the reader said "this lesion is not measurable", the very first save
     * must say so too. Without this the create POST invented "measurable" and
     * the form then re-read that invented value back off the server.
     *
     * Technical context
     * -----------------
     * D7-7584. The form attaches `payload.measurementData.isMeasurable` on the
     * same tool actions that carry `permanentData`. `prepareRequestData` reads
     * measurability off `currentMeasurement.measurementData`, which does not
     * exist yet on a create, so it fell through to a hardcoded `true`.
     * Capturing the value here gives that resolution a form-supplied fallback
     * to prefer over the default — see its `resolvedIsMeasurable`.
     */
    const formIsMeasurable = formPayload?.measurementData?.isMeasurable;

    state.activeMeasurement = {
      measurementCode: p.measurementConfig.code,
      instrumentCode: p.instrumentConfig.code,
      measurementConfigId: p.measurementConfig.id ?? undefined,
      instrumentConfigId: p.instrumentConfig.id ?? undefined,
      measurementId: p.measurementId,
      index: p.index,
      ...(formPermanentData ? { permanentData: formPermanentData } : {}),
      ...(typeof formIsMeasurable === 'boolean'
        ? { isMeasurable: formIsMeasurable }
        : {}),
    };
  }
}

export const measurementsSlice = createSlice({
  name: 'measurements',
  initialState,

  selectors: {
    getActiveMeasurement: state => state.activeMeasurement,
    getSliceState: state => state,
    getMeasurements: state => Object.values(state.data),
    getScreenshot: state => state.screenshotState,
    getViewerReady: state => state.viewerReady,
    getWritePending: state => state.writePending > 0,
  },
  reducers: {
    // `writePending` is deliberately NOT reset: this runs on a reading switch, and
    // zeroing it under a live write let that write's own `finally` decrement a count
    // a second, still-pending write needed. The write worker's `finally` runs on
    // every exit — success, throw, timeout cancellation and saga teardown — so the
    // count drains on its own.
    resetState: state => {
      state.loading = false;
      state.activeMeasurement = null;
      state.error = undefined;
      state.data = {};
      state.screenshotState = {};
      state.viewerReady = false;
      state.measurementsLoaded = false;
    },
    reviveMeasurements: act<Measurement[]>(measurements => ({
      data: _.keyBy(measurements, 'id'),
      measurementsLoaded: true,
    })),
    activateMeasurement:
      actMutate<MeasurementsHandleToolAction['payload']>(setActiveMeasurement),
    showMeasurement:
      actMutate<MeasurementsHandleToolAction['payload']>(setActiveMeasurement),
    hideMeasurement:
      actMutate<MeasurementsHandleToolAction['payload']>(setActiveMeasurement),
    getMeasurements: actEmpty(),
    deactivateMeasurement: act<MeasurementsHandleToolAction['payload']>(() => ({
      activeMeasurement: null,
    })),
    // Never dispatched — both are built as arguments to `yield call(worker, ...)`
    // so the caller can read the outcome.
    createMeasurement: actEmpty<MeasurementsCreateAction['payload']>(),
    createMeasurementSuccess: actMutate<
      MeasurementsCreateUpdateSuccessAction['payload']
    >((ms, state) => {
      state.data[ms.id] = ms;
    }),
    createMeasurementFailure: actReject('error', null),

    updateMeasurement: actEmpty<MeasurementsUpdateAction['payload']>(),
    updateMeasurementSuccess: actMutate<
      MeasurementsCreateUpdateSuccessAction['payload']
    >((ms, state) => {
      state.data[ms.id] = ms;
    }),
    updateMeasurementFailure: actReject('error', null),

    // The only writer of `writePending`, dispatched from the write worker's
    // `try`/`finally`. Not the success actions: `createNAMeasurement`,
    // `screenshotNAWorker` and `executePermanentCleanup` dispatch those after
    // their own `api.*` writes and never raised the flag.
    //
    // The payload is this one worker's state; the slice keeps the count, because
    // writes for different annotations overlap under `takeQueue`.
    //
    // Anything waiting for a write to SETTLE must take `isMeasurementWriteSettled`
    // rather than this action's type (a bare type matches the `true` dispatch too)
    // AND re-read the count, because one settle does not mean none are left.
    setMeasurementWritePending: actMutate<boolean>((pending, state) => {
      state.writePending = pending
        ? state.writePending + 1
        : Math.max(0, state.writePending - 1);
    }),

    persistMeasurementUpdate:
      actMutate<MeasurementsHandleToolAction['payload']>(setActiveMeasurement),

    deleteMeasurement:
      actReq<MeasurementsDeleteAction['payload']>('deletePending'),
    deleteMeasurementSuccess: act<MeasurementsDeleteAction['payload']>(
      ({ measurementId }, state) => ({
        data: _.omit(state.data, [measurementId]),
        deletePending: false,
        activeMeasurement: null,
      })
    ),
    deleteMeasurementFailure: actReject('deletePending'),
    removeMeasurement:
      actReq<MeasurementsHandleToolAction['payload']>('deletePending'),
    removeMeasurementSuccess: act<MeasurementsDeleteAction['payload']>(
      ({ measurementId }, state) => ({
        data: _.omit(state.data, [measurementId]),
        deletePending: false,
        activeMeasurement: null,
      })
    ),
    removeMeasurementFailure: actReject('deletePending'),

    lockMeasurement: actMutate<MeasurementsHandleToolAction['payload']>(
      (p, state) => {
        setActiveMeasurement(p, state);
      }
    ),
    lockMeasurementSuccess: actMutate<
      MeasurementsEmptySuccessAction['payload']
    >((p, state) => {
      const measurement = state.data[p.measurementId];

      if (measurement) {
        // Flip isLocked on both the legacy singular and every entry in
        // annotations[]. Multi-annotation records previously had their array
        // entries left in their pre-lock state in the store; the viewer saga
        // walks getAnnotations(m) but the persisted state is the source of
        // truth for the form's lock chips.
        if (measurement.instrumentStateJson?.annotation) {
          measurement.instrumentStateJson.annotation.isLocked = true;
        }
        for (const a of measurement.instrumentStateJson?.annotations ?? []) {
          a.isLocked = true;
        }
        measurement.locked = true;
      }
    }),
    lockMeasurementFailure: actReject(),

    /**
     * Brackets an update→lock→confirm chain for one measurement (D7-7565).
     *
     * Plain terms
     * -----------
     * "A lock for this lesion has started" / "…has finished". Between the two,
     * any save the viewer echoes for the same lesion waits instead of racing
     * the lock's own save.
     *
     * Technical
     * ---------
     * `lockMeasurementSuccess` is too late to gate on: it fires only AFTER the
     * lock REST call resolves, so `locked` is still false for the whole window
     * the race lives in. Dispatched by `lockUnlockMeasurementsWorker`
     * (`shouldLock` only) and `screenshotNAWorker`; `endLockSequence` runs from
     * a `finally`, so every exit path — success, failure, saga cancellation —
     * settles the counter.
     */
    beginLockSequence: actMutate<LockSequencePayload>((p, state) => {
      incLockPending(state, p.measurementId);
    }),
    endLockSequence: actMutate<LockSequencePayload>((p, state) => {
      decLockPending(state, p.measurementId);
    }),

    unlockMeasurement: actMutate<MeasurementsHandleToolAction['payload']>(
      (p, state) => {
        setActiveMeasurement(p, state);
      }
    ),
    unlockMeasurementSuccess: actMutate<
      MeasurementsEmptySuccessAction['payload']
    >((p, state) => {
      const measurement = state.data[p.measurementId];
      if (measurement) {
        if (measurement.instrumentStateJson?.annotation) {
          measurement.instrumentStateJson.annotation.isLocked = false;
        }
        for (const a of measurement.instrumentStateJson?.annotations ?? []) {
          a.isLocked = false;
        }
        measurement.locked = false;
      }
    }),
    unlockMeasurementFailure: actReject(),

    screenshotMeasurement: actMutate<MeasurementsHandleToolAction['payload']>(
      (p, state) => {
        state.loading = true;
        setActiveMeasurement(p, state);
      }
    ),
    // Confirm a no-screenshot lock against BE (notAvailable=true). Handled by
    // screenshotNAWorker — backend-only, no viewer round-trip. Does NOT set
    // state.loading: that flag drives the screenshot-capture overlay, which this
    // path never triggers — matches lockMeasurement/unlockMeasurement, not
    // screenshotMeasurement.
    screenshotNAMeasurement: actMutate<
      MeasurementsHandleToolAction['payload']
    >((p, state) => {
      setActiveMeasurement(p, state);
    }),
    screenshotMeasurementSuccess: actMutate<
      MeasurementScreenshotSuccessAction['payload']
    >((p, state) => {
      return { ...state.screenshotState, ...p };
    }),
    screenshotMeasurementFailure: actReject(),
    // Terminal signal for the screenshot upload pipeline — fires once at the end
    // of getScreenshotMeasurementsWorker. screenshotMeasurementSuccess is also
    // dispatched inside the polling loop, so consumers that need to await the
    // real finish must use these terminal actions, not Success.
    screenshotMeasurementUploadSuccess: actMutate<
      MeasurementScreenshotSuccessAction['payload']
    >((p, state) => {
      return { ...state.screenshotState, ...p };
    }),
    screenshotMeasurementUploadFailure: actReject(),
    setViewerReady: (state, action) => {
      state.viewerReady = action.payload;
    },

    // Multi-annotation: enqueue an append for the named measurement. The
    // coordinator saga creates a per-measurement actionChannel so writes to
    // the same record are processed strictly in order (R1 mitigation).
    enqueueAppendAnnotation: actMutate<EnqueueAppendAnnotationPayload>(
      (p, state) => {
        incAppendPending(state, p.measurementId);
      }
    ),
    enqueueRemoveAnnotation: actMutate<EnqueueRemoveAnnotationPayload>(
      (p, state) => {
        incAppendPending(state, p.measurementId);
      }
    ),
    /**
     * A viewer-echoed `update` deferred onto the same per-measurement channel
     * because a lock sequence is in flight for that record (D7-7565).
     *
     * Deliberately does NOT touch `appendPendingByMeasurement`: that counter
     * drives the form's Add/trash disabled state for sub-annotation edits, and
     * a queued geometry echo is not reader-initiated work to block the UI on.
     * The channel worker settles this action by running
     * `updateMeasurementsWorker`, which raises `writePending` itself.
     */
    enqueueUpdateMeasurement: actEmpty<EnqueueUpdateMeasurementPayload>(),
    appendAnnotationSuccess: actMutate<
      MeasurementsCreateUpdateSuccessAction['payload']
    >((ms, state) => {
      state.data[ms.id] = ms;
      decAppendPending(state, ms.id);
      delete state.appendRetryBufferByMeasurement[ms.id];
    }),
    appendAnnotationFailure: actMutate<AppendAnnotationFailurePayload>(
      (p, state) => {
        decAppendPending(state, p.measurementId);
        if (p.retriable && p.retryEntry) {
          state.appendRetryBufferByMeasurement[p.measurementId] = p.retryEntry;
        } else {
          delete state.appendRetryBufferByMeasurement[p.measurementId];
        }
      }
    ),
    /**
     * Form trigger to retry the last failed append/remove for this measurement.
     * Reads the buffered payload and re-enqueues via the existing per-measurement
     * actionChannel. No-op if the buffer is empty.
     */
    retryAppendAnnotation: actEmpty<RetryAppendAnnotationPayload>(),
    /**
     * Form trigger to drop the retry buffer without retrying (e.g., the reader
     * dismisses the error toast).
     */
    clearAppendRetry: actMutate<RetryAppendAnnotationPayload>((p, state) => {
      delete state.appendRetryBufferByMeasurement[p.measurementId];
    }),
  },
});

export const actions = {
  ...measurementsSlice.actions,
};
export const selectors = measurementsSlice.selectors;

// `take(setMeasurementWritePending.type)` would also match the `true` dispatch that
// OPENS a write, so a waiter would be woken by the next write instead of the one it
// is waiting for. Exported as a named reference so `@test/saga` can assert the
// effect — two separately built predicates are never `toEqual`.
export const isMeasurementWriteSettled = (action: {
  type?: string;
  payload?: unknown;
}): boolean =>
  action?.type === measurementsSlice.actions.setMeasurementWritePending.type &&
  action?.payload === false;

export const useMeasurementsSlice = createSliceHook(
  measurementsSlice.name,
  actions
);

export const selectMeasurementsState = createSelector('measurements');
